import type { Brand } from '@curology/ui-components-web/brand';

import { PAYMENT_LINE_ITEM_LABELS } from '@/consts/payments';
import type {
  PaymentInvoice,
  PaymentRequestOptions,
} from '@/types/payments/types';
import type { Estimate } from '@/types/purchaseContextToggle/types';

const getTotalLabel = (brand: Brand): string => {
  switch (brand) {
    case 'curology':
      return PAYMENT_LINE_ITEM_LABELS.CUROLOGY_TOTAL;
    case 'agency':
      return PAYMENT_LINE_ITEM_LABELS.AGENCY_TOTAL;
    default:
      throw new Error(`Unknown brand ${brand}`);
  }
};

const getSubtotalLabel = (isInitialRecurringPurchase: boolean) =>
  isInitialRecurringPurchase
    ? PAYMENT_LINE_ITEM_LABELS.TRIAL_PURCHASE_SUBTOTAL
    : PAYMENT_LINE_ITEM_LABELS.PURCHASE_SUBTOTAL;

const getEstimateAmounts = (estimate: Estimate) => {
  return {
    discount: Math.abs(estimate.adjustmentAmount.amount),
    shippingAndHandling:
      (estimate.shipping?.amount || 0) + (estimate.handling?.amount || 0),
    purchaseSubtotal: estimate.originalAmount.amount,
    preTaxSubtotal: estimate.finalAmount.amount,
    tax: estimate.tax.amount,
    total: Math.max(estimate.total.amount, 0),
  };
};

const getLineItem = (amount: number, label: string) => ({ amount, label });

export const createPaymentInvoice = (
  brand: Brand,
  estimate: Estimate,
  futureEstimate: Estimate | undefined = undefined,
): PaymentInvoice => {
  const amounts = getEstimateAmounts(estimate);
  const futureAmounts = futureEstimate
    ? getEstimateAmounts(futureEstimate)
    : undefined;

  const total = getLineItem(amounts.total, getTotalLabel(brand));

  const discount = amounts.discount
    ? [getLineItem(-amounts.discount, PAYMENT_LINE_ITEM_LABELS.DISCOUNT)]
    : [];

  const futureSubtotal = futureAmounts
    ? [
        getLineItem(
          futureAmounts.preTaxSubtotal,
          PAYMENT_LINE_ITEM_LABELS.TRIAL_PURCHASE_FUTURE_SUBTOTAL,
        ),
      ]
    : [];

  const paymentLineItems = [
    getLineItem(amounts.purchaseSubtotal, getSubtotalLabel(!!futureEstimate)),
    ...discount,
    getLineItem(amounts.shippingAndHandling, PAYMENT_LINE_ITEM_LABELS.SHIPPING),
    getLineItem(amounts.tax, PAYMENT_LINE_ITEM_LABELS.TAX),
    ...futureSubtotal,
  ];

  const shippingOptions = futureEstimate
    ? [
        {
          amount: amounts.shippingAndHandling,
          id: 'basic',
          label: PAYMENT_LINE_ITEM_LABELS.TRIAL_PURCHASE_SHIPPING_OPTION,
        },
      ]
    : [];

  return { paymentLineItems, shippingOptions, total };
};

export const createPaymentRequestOptions = (
  requestPayerInfo = false,
): PaymentRequestOptions => {
  return {
    requestPayerName: requestPayerInfo,
    requestPayerEmail: false,
    requestShipping: requestPayerInfo,
    requestPayerPhone: requestPayerInfo,
  };
};
